import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Loader2 } from 'lucide-react';
import api from '../utils/api';

const Dashboard = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  // Sidebar collapsed by default on mobile (window width < 1024px)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : true
  );

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/api/chat/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, []);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/');
    } else if (user) {
      fetchConversations();
    }
  }, [user, isLoading, navigate, fetchConversations]);

  const handleNewChat = async () => {
    try {
      const response = await api.post('/api/chat/conversations', { title: 'New Chat' });
      const newConv = response.data;
      setConversations(prev => [newConv, ...prev]);
      navigate(`/dashboard/chat/${newConv.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Fallback to local creation
      const newConv = {
        id: `conv_${Date.now()}`,
        title: 'New Chat',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: []
      };
      setConversations(prev => [newConv, ...prev]);
      navigate(`/dashboard/chat/${newConv.id}`);
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      await api.delete(`/api/chat/conversations/${id}`);
      setConversations(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setConversations(prev => prev.filter(c => c.id !== id));
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="h-screen flex bg-zinc-950">
      <Sidebar
        conversations={conversations}
        onNewChat={handleNewChat}
        onDeleteChat={handleDeleteChat}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex-1 overflow-y-auto">
          <Outlet context={{ conversations, setConversations, refreshConversations: fetchConversations }} />
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
